import {
  ArrowsVertical,
  Column,
  Download,
  Filter,
  FilterRemove,
  ResetAlt,
  SortAscending,
  SortDescending,
} from '@carbon/react/icons';
import style from './table-actions.module.scss';
import { OverflowMenu, OverflowMenuItem, Button, Modal } from '@carbon/react';
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Toggle } from 'components/toggle/toggle';
import { DebouncedInput } from 'components/debounced-input';
import classNames from 'classnames';
import { RowData, Table } from '@tanstack/react-table';
import plur from 'plur';
import { useCsvExport } from 'lib/hooks/useCsvExport';
import {
  VolumeSourceConfig,
  AfeSourceConfig,
} from 'components/nri-table/types';

export type ExportFormat = 'csv' | 'xlsx';

// Server-side export preset shape consumed by this component.
// Kept independent of `components/data-table/types` so the NRI flow does
// not depend on the data-table package.
export interface ExportPreset {
  name: string;
  formats: ExportFormat[];
  isCurrentView?: boolean;
  filenameSuffix?: string;
}

declare module '@tanstack/react-table' {
  // allows us to define custom properties for our columns
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'dropdown';
    toggleColumnFilter?: boolean;
    isColumnExportable?: boolean;
    getExportValue?: (row: TData) => unknown;
    infoTooltip?: string;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    reset?: {
      resetCustomizations: () => void;
    };
    onTractClick?: (tractId: string, subtractId?: string) => void;
    volumeSourceConfig?: VolumeSourceConfig;
    afeSourceConfig?: AfeSourceConfig;
    /** The package the table belongs to, for links into its wizard; absent off a package page. */
    packageId?: number;
  }
}

export interface ServerExportParams {
  filters?: Record<string, unknown>;
  sort?: { column: string; direction: string };
  columnOrder?: string[];
  columnPresetName?: string;
  columnOrderExclusive?: boolean;
  filenameSuffix?: string;
}

interface Props<T> {
  table: Table<T>;
  isColumnFilterActive: boolean;
  setIsColumnFilterActive: (active: boolean) => void;
  hideColumnFilterButton?: boolean;
  showExport?: boolean;
  /** Server-side export callback. When provided, uses server-side export instead of client-side. */
  onServerExport?: (params: ServerExportParams) => void;
  /** Whether server-side export is in progress */
  isServerExporting?: boolean;
  /**
   * Named column ordering presets shown as dropdown items.
   * - 0 or undefined: single button, no preset
   * - 1 entry: auto-apply preset, single button
   * - 2+ entries: dropdown to pick preset
   * Each preset includes its name and supported output formats.
   */
  exportPresets?: ExportPreset[];
}

export function TableActions<T>({
  table,
  isColumnFilterActive,
  setIsColumnFilterActive,
  hideColumnFilterButton = false,
  showExport = false,
  onServerExport,
  isServerExporting = false,
  exportPresets,
}: Props<T>) {
  const { exportToCsv, isExporting } = useCsvExport();

  const columns = table.getAllColumns();
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | null>(null);
  const [filterBy, setFilterBy] = useState('');
  const [showResetWarning, setShowResetWarning] = useState(false);

  const orderedColumns = sortOrder
    ? columns.sort((a, b) => {
        const aHeader = a.columnDef.header?.toString() ?? '';
        const bHeader = b.columnDef.header?.toString() ?? '';
        return aHeader.localeCompare(bHeader) * (sortOrder === 'ASC' ? 1 : -1);
      })
    : columns;

  const filteredColumns = orderedColumns.filter((col) => {
    const colHeader = col.columnDef.header?.toString() ?? '';
    return (
      colHeader.toLowerCase().startsWith(filterBy) &&
      col.columnDef.meta?.toggleColumnFilter !== false
    );
  });

  // Only count user-toggleable columns (exclude columns with toggleColumnFilter: false)
  const toggleableColumns = orderedColumns.filter(
    (col) => col.columnDef.meta?.toggleColumnFilter !== false
  );
  const hiddenColumnCount = toggleableColumns.filter(
    (col) => !col.getIsVisible()
  ).length;
  const visibleColumnCount = toggleableColumns.length - hiddenColumnCount;

  const handleShowAll = () => {
    // Only show columns that are user-toggleable
    columns.forEach((col) => {
      if (col.columnDef.meta?.toggleColumnFilter !== false) {
        col.getToggleVisibilityHandler()({ target: { checked: true } });
      }
    });
  };

  const handleHideAll = () => {
    // Only hide columns that are user-toggleable
    columns.forEach((col) => {
      if (col.columnDef.meta?.toggleColumnFilter !== false) {
        col.getToggleVisibilityHandler()({ target: { checked: false } });
      }
    });
  };

  const handleReset = () => {
    table.resetColumnFilters(true);
    table.resetColumnSizing(true);
    table.resetColumnVisibility(true);
    table.resetColumnOrder();
    table.resetColumnPinning();
    table.options.meta?.reset?.resetCustomizations();
    setShowResetWarning(false);
  };

  const handleExport = async () => {
    const allColumns = table.getAllColumns();
    const exportableColumns = allColumns.filter(
      (col) => col.columnDef.meta?.isColumnExportable !== false
    );
    await exportToCsv({
      filename: 'export',
      columns: exportableColumns.map((col) => ({
        key: col.id,
        header: col.columnDef.header?.toString() ?? '',
      })),
      data: table.getRowModel().rows.map((row) => {
        const rowData: Record<string, unknown> = {};
        exportableColumns.forEach((col) => {
          const cell = row.getAllCells().find((c) => c.column.id === col.id);
          if (cell) {
            // Use custom export getter if provided, otherwise use cell value
            const getExportValue = col.columnDef.meta?.getExportValue;
            if (getExportValue) {
              rowData[col.id] = getExportValue(row.original);
            } else {
              rowData[col.id] = cell.getValue();
            }
          }
        });
        return rowData;
      }),
    });
  };

  const renderSortIcon = () => {
    if (sortOrder === 'ASC') {
      return <SortDescending className={style.sortIcon}></SortDescending>;
    } else if (sortOrder === 'DESC') {
      return <SortAscending className={style.sortIcon}></SortAscending>;
    } else {
      return <ArrowsVertical className={style.sortIcon}></ArrowsVertical>;
    }
  };

  const toggleColumnFilter = () => {
    setIsColumnFilterActive(!isColumnFilterActive);
  };

  const DEFAULT_PRESET: ExportPreset = useMemo(
    () => ({ name: 'Export', formats: ['csv'] }),
    []
  );

  const effectivePresets = useMemo(() => {
    return exportPresets && exportPresets.length > 0
      ? exportPresets
      : [DEFAULT_PRESET];
  }, [exportPresets, DEFAULT_PRESET]);

  const buildServerExportParams = (
    preset?:
      Pick<ExportPreset, 'name' | 'isCurrentView' | 'filenameSuffix'> | string
  ): ServerExportParams => {
    const columnFilters = table.getState().columnFilters;
    const sorting = table.getState().sorting;
    const filters: Record<string, unknown> = {};
    columnFilters.forEach(({ id, value }) => {
      if (value !== undefined && value !== '') {
        filters[id] = value;
      }
    });
    const sort =
      sorting.length > 0
        ? { column: sorting[0].id, direction: sorting[0].desc ? 'desc' : 'asc' }
        : undefined;

    const visibleIds = table
      .getVisibleLeafColumns()
      .map((col) => col.id)
      .filter((id) => id !== 'expander');

    const isCurrentView =
      preset === null ||
      preset === undefined ||
      (typeof preset === 'object' && preset !== null && preset.isCurrentView);

    const filenameSuffix =
      typeof preset === 'object' && preset !== null
        ? preset.filenameSuffix
        : undefined;

    if (isCurrentView) {
      return {
        filters,
        sort,
        columnOrder: visibleIds,
        columnOrderExclusive: true,
        ...(filenameSuffix ? { filenameSuffix } : {}),
      };
    }

    const hiddenIds = table
      .getAllLeafColumns()
      .filter((col) => !col.getIsVisible() && col.id !== 'expander')
      .map((col) => col.id);
    const columnOrder = [...visibleIds, ...hiddenIds];
    const columnPresetName = typeof preset === 'string' ? preset : preset?.name;

    return {
      filters,
      sort,
      columnOrder,
      columnPresetName,
      ...(filenameSuffix ? { filenameSuffix } : {}),
    };
  };

  const isExportDisabled =
    isExporting || isServerExporting || table.getRowModel().rows.length === 0;

  const handlePresetClick = (preset: ExportPreset) => {
    if (onServerExport) {
      const { name, isCurrentView, filenameSuffix } = preset;
      onServerExport(
        buildServerExportParams({ name, isCurrentView, filenameSuffix })
      );
    } else {
      handleExport();
    }
  };

  const renderExportControl = () => {
    if (effectivePresets.length >= 2) {
      return (
        <OverflowMenu
          renderIcon={Download}
          flipped
          size="lg"
          iconDescription={
            isServerExporting ? 'Export in progress...' : 'Export'
          }
          className={style.exportMenu}
          menuOptionsClass="exportMenuOptions"
          disabled={isExportDisabled}
        >
          {effectivePresets.map((preset) => (
            <OverflowMenuItem
              key={preset.name}
              itemText={`${preset.name} (.${preset.formats[0]})`}
              onClick={() => handlePresetClick(preset)}
            />
          ))}
        </OverflowMenu>
      );
    }

    return (
      <Button
        hasIconOnly
        renderIcon={Download}
        disabled={isExportDisabled}
        kind="ghost"
        size="lg"
        iconDescription={isServerExporting ? 'Export in progress...' : 'Export'}
        onClick={() => handlePresetClick(effectivePresets[0])}
      />
    );
  };

  return (
    <>
      {createPortal(
        <Modal
          open={showResetWarning}
          modalHeading="Reset table to default"
          danger
          size="xs"
          primaryButtonText="Reset to default"
          secondaryButtonText="Cancel"
          className={style.resetTableModal}
          onRequestClose={() => setShowResetWarning(false)}
          onRequestSubmit={() => handleReset()}
        >
          <p>
            Are you sure you want to reset the table to its default settings?
          </p>
          <p>This action will revert all changes and cannot be undone.</p>
        </Modal>,
        document.body
      )}
      <div className={style.tableActions}>
        {!hideColumnFilterButton && (
          <Button
            hasIconOnly
            onClick={toggleColumnFilter}
            kind="ghost"
            size="lg"
            iconDescription="Show/Hide filters"
            renderIcon={isColumnFilterActive ? FilterRemove : Filter}
            className={isColumnFilterActive ? style.activeButton : undefined}
            data-testid="table-filter-button"
          />
        )}
        {hiddenColumnCount ? (
          <span className={style.columnsCount}>
            {`${hiddenColumnCount} ${plur('column', hiddenColumnCount)} hidden`}
          </span>
        ) : null}

        <OverflowMenu
          data-testid="column-visibility-menu"
          className={style.tableOverflowMenu}
          renderIcon={Column}
          flipped
          size="lg"
          iconDescription="Show/Hide columns"
        >
          <>
            <div className={style.menu}>
              <p className={style.title}>Columns ({columns.length})</p>
              <div className={style.scrollableArea}>
                <div className={style.shortcut}>
                  <button
                    data-testid="hide-all-columns"
                    className={classNames(
                      visibleColumnCount
                        ? style.ghostButton
                        : style.ghostButtonDisabled
                    )}
                    onClick={() => handleHideAll()}
                  >
                    Hide All
                  </button>
                  <button
                    data-testid="show-all-columns"
                    onClick={() => handleShowAll()}
                    className={classNames(
                      hiddenColumnCount
                        ? style.ghostButton
                        : style.ghostButtonDisabled
                    )}
                  >
                    Show All
                  </button>
                  <span
                    onClick={() =>
                      setSortOrder((sortOrder) => {
                        return sortOrder === 'ASC' ? 'DESC' : 'ASC';
                      })
                    }
                  >
                    {renderSortIcon()}
                  </span>
                </div>

                <div className={style.searchArea}>
                  <DebouncedInput
                    value={''}
                    onChange={(text) => {
                      setFilterBy(text.toString().toLocaleLowerCase());
                    }}
                    placeholder="Search columns..."
                  />
                </div>
                {filteredColumns.map((column) => (
                  <div
                    key={column.id}
                    className={style.columnItem}
                    data-testid={`column-item-${column.id}`}
                  >
                    <p className={style.columnName}>
                      {column.columnDef.header?.toString() ?? ''}
                    </p>
                    <div>
                      <Toggle
                        data-testid={`column-toggle-${column.id}`}
                        isActive={column.getIsVisible()}
                        handleToggle={(visibility) =>
                          column.getToggleVisibilityHandler()({
                            target: { checked: visibility },
                          })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        </OverflowMenu>
        <Button
          hasIconOnly
          renderIcon={ResetAlt}
          kind="ghost"
          size="lg"
          iconDescription="Reset table to default"
          onClick={() => setShowResetWarning(true)}
        />
        {showExport && renderExportControl()}
      </div>
    </>
  );
}
